/**
 * Board Layout Utilities
 * 
 * Provides a consistent algorithm for persisting and rendering board item
 * layouts across different screen sizes (4-col and 6-col grids).
 * 
 * The @amzn/awsui-board-components Board component uses:
 * - columnSpan: number of columns an item occupies in the current grid
 * - columnOffset: an object mapping each board column count to the item's
 *   starting column position, e.g. { 4: 0, 6: 2 }
 * 
 * TWO DATA FORMATS ARE SUPPORTED:
 * 
 * 1. LEGACY FORMAT (items saved before this module):
 *    - column_span: a stored value (0-6) mapped via columnSpanToUseMapper
 *    - column_offset: JSON object like {'4': 0, '6': 0} (no '_spans' key)
 *    - Detected by: column_offset is a JSON object WITHOUT a '_spans' key
 *    - Behavior: uses the original columnSpanToUseMapper logic (computeSpanFromLegacy)
 * 
 * 2. NEW FORMAT (items saved via this module):
 *    - column_span: the actual rendered span on the screen that saved it
 *    - column_offset: JSON object with offsets AND pre-computed spans for all
 *      grid sizes, e.g. {'4': 0, '6': 0, '_spans': {'4': 2, '6': 3}}
 *    - Detected by: column_offset has a '_spans' key
 *    - Behavior: reads the pre-computed span for the current screen directly
 * 
 * SAVE ALGORITHM (getLayoutForSaveAll):
 * Items are grouped into rows based on their current offsets and spans.
 * Each row is scaled as a unit to the other grid size, ensuring:
 * - Items on the same row stay on the same row (no unexpected wrapping)
 * - Deliberate gaps between items are preserved proportionally
 * - Spans are distributed using largest-remainder proportional allocation
 * 
 * BOARD GRID SIZES:
 * - Medium screens (< 2160px): Board renders with 4 columns  
 * - Large screens (>= 2160px): Board renders with 6 columns
 */

// The board column counts we support
const SUPPORTED_BOARD_COLUMNS = [4, 6];

/**
 * Determines the number of board columns for the current screen width.
 * @param {number} screenWidth - Current viewport width in pixels
 * @returns {number} Number of columns the Board component uses
 */
export function getBoardColumnsForWidth(screenWidth) {
    return screenWidth >= 2160 ? 6 : 4;
}

/**
 * Scales a set of items on a single row from one grid size to another.
 * 
 * The algorithm distributes the target columns among items proportionally
 * to their source spans, ensuring:
 * 1. Every item gets at least 1 column
 * 2. The total used columns (spans + gaps) doesn't exceed target grid
 * 3. Deliberate gaps are preserved proportionally
 * 
 * @param {Array} rowItems - Items on the same row: [{span, offset}, ...]
 * @param {number} sourceColumns - Source grid column count
 * @param {number} targetColumns - Target grid column count
 * @returns {Array} Items with computed target spans and offsets
 */
function scaleRow(rowItems, sourceColumns, targetColumns) {
    if (sourceColumns === targetColumns) {
        return rowItems.map(item => ({ ...item, targetSpan: item.span, targetOffset: item.offset }));
    }

    const ratio = targetColumns / sourceColumns;

    // Scale each item's span proportionally, ensuring minimum of 1
    // Use a largest-remainder method to ensure spans sum correctly
    const rawSpans = rowItems.map(item => item.span * ratio);
    const flooredSpans = rawSpans.map(s => Math.max(1, Math.floor(s)));
    
    // Calculate how much total span we've used vs what's available
    // We need to account for gaps too
    const totalSourceUsed = rowItems.length > 0 
        ? rowItems[rowItems.length - 1].offset + rowItems[rowItems.length - 1].span 
        : 0;
    const totalTargetAvailable = Math.round(totalSourceUsed * ratio);
    const totalFlooredSpans = flooredSpans.reduce((a, b) => a + b, 0);
    
    // Calculate gaps between items in source
    const sourceGaps = [];
    for (let i = 0; i < rowItems.length; i++) {
        if (i === 0) {
            sourceGaps.push(rowItems[i].offset); // gap before first item
        } else {
            const prevEnd = rowItems[i - 1].offset + rowItems[i - 1].span;
            sourceGaps.push(rowItems[i].offset - prevEnd); // gap between items
        }
    }

    // Scale gaps proportionally
    const targetGaps = sourceGaps.map(g => Math.round(g * ratio));
    const totalTargetGaps = targetGaps.reduce((a, b) => a + b, 0);

    // Distribute remaining columns to spans (largest remainder method)
    let remainingForSpans = Math.min(targetColumns, totalTargetAvailable) - totalTargetGaps;
    remainingForSpans = Math.max(rowItems.length, remainingForSpans); // at least 1 per item
    
    // Allocate spans proportionally
    const totalSourceSpans = rowItems.reduce((a, item) => a + item.span, 0);
    const targetSpans = rowItems.map((item, i) => {
        const proportion = item.span / totalSourceSpans;
        return Math.max(1, Math.floor(proportion * remainingForSpans));
    });
    
    // Distribute leftover columns using largest remainder
    let allocated = targetSpans.reduce((a, b) => a + b, 0);
    const remainders = rowItems.map((item, i) => {
        const proportion = item.span / totalSourceSpans;
        return (proportion * remainingForSpans) - targetSpans[i];
    });
    
    while (allocated < remainingForSpans) {
        // Find the item with the largest remainder
        let maxIdx = 0;
        for (let i = 1; i < remainders.length; i++) {
            if (remainders[i] > remainders[maxIdx]) maxIdx = i;
        }
        targetSpans[maxIdx]++;
        remainders[maxIdx] -= 1;
        allocated++;
    }

    // Compute target offsets based on gaps
    const result = rowItems.map((item, i) => {
        let targetOffset = targetGaps.slice(0, i + 1).reduce((a, b) => a + b, 0);
        // Add spans of previous items
        for (let j = 0; j < i; j++) {
            targetOffset += targetSpans[j];
        }
        // Ensure item doesn't overflow
        const maxOffset = targetColumns - targetSpans[i];
        targetOffset = Math.max(0, Math.min(maxOffset, targetOffset));
        
        return {
            ...item,
            targetSpan: targetSpans[i],
            targetOffset: targetOffset,
        };
    });

    return result;
}

/**
 * Groups items into rows based on their offsets and spans.
 * Items are on the same row if they don't overlap vertically
 * (determined by offset positions fitting within the grid).
 * 
 * @param {Array} items - Array of {span, offset, index} objects
 * @param {number} columns - Grid column count
 * @returns {Array} Array of rows, each row is an array of items
 */
function groupIntoRows(items, columns) {
    if (!items || items.length === 0) return [];
    
    // Sort by offset to process left-to-right
    const sorted = [...items].sort((a, b) => a.offset - b.offset);
    
    const rows = [];
    let currentRow = [sorted[0]];
    let rowEnd = sorted[0].offset + sorted[0].span;

    for (let i = 1; i < sorted.length; i++) {
        const item = sorted[i];
        if (item.offset >= rowEnd && rowEnd + item.span <= columns + 1) {
            // Item fits on current row (starts after previous item ends)
            // Allow slight overflow tolerance for the Board's placement
            currentRow.push(item);
            rowEnd = item.offset + item.span;
        } else if (item.offset < rowEnd) {
            // Item overlaps with current row — might be on same row if it fits
            // The Board component can place items that overlap in offset on different rows
            // Start a new row
            rows.push(currentRow);
            currentRow = [item];
            rowEnd = item.offset + item.span;
        } else {
            // Item starts after current row but would overflow grid
            rows.push(currentRow);
            currentRow = [item];
            rowEnd = item.offset + item.span;
        }
    }
    rows.push(currentRow);
    
    return rows;
}

/**
 * Transforms raw DB item data into render-ready layout properties.
 * 
 * Handles two formats transparently:
 * - Legacy: column_offset has no '_spans' key → uses computeSpanFromLegacy
 *   (replicates original columnSpanToUseMapper behavior exactly)
 * - New: column_offset has '_spans' key → reads pre-computed span directly
 * 
 * @param {object} item - Raw board item from the database
 * @param {number} screenWidth - Current viewport width
 * @returns {object} { columnSpan, columnOffset, rowSpan }
 */
export function getRenderedLayout(item, screenWidth) {
    const boardColumns = getBoardColumnsForWidth(screenWidth);
    const rowSpan = Number(item.row_span) || 2;
    const rawSpan = Number(item.column_span);
    const rawOffset = item.column_offset;

    let offsetObj = parseStoredOffset(rawOffset);

    let columnSpan;
    let columnOffsetMap = {};

    if (offsetObj && offsetObj._spans) {
        // New format: has pre-computed spans for each grid size
        const storedSpan = offsetObj._spans[String(boardColumns)];
        if (storedSpan != null) {
            columnSpan = Number(storedSpan);
        } else {
            // Fallback: simple proportional scale
            const firstKey = Object.keys(offsetObj._spans)[0];
            const srcSpan = Number(offsetObj._spans[firstKey]);
            const srcCols = Number(firstKey);
            columnSpan = Math.max(1, Math.min(boardColumns, Math.round(srcSpan * boardColumns / srcCols)));
        }
        for (const cols of SUPPORTED_BOARD_COLUMNS) {
            columnOffsetMap[cols] = Number(offsetObj[String(cols)] ?? 0);
        }
    } else if (offsetObj && typeof offsetObj === 'object' && !offsetObj._spans) {
        // Legacy format
        columnSpan = computeSpanFromLegacy(rawSpan, boardColumns);
        for (const cols of SUPPORTED_BOARD_COLUMNS) {
            columnOffsetMap[cols] = Number(offsetObj[String(cols)] ?? 0);
        }
    } else {
        // Fallback
        columnSpan = computeSpanFromLegacy(rawSpan, boardColumns);
        const offset = Number(rawOffset) || 0;
        for (const cols of SUPPORTED_BOARD_COLUMNS) {
            columnOffsetMap[cols] = cols === boardColumns ? offset : 0;
        }
    }

    return { columnSpan, columnOffset: columnOffsetMap, rowSpan };
}

/**
 * Computes the rendered column span from a legacy stored value (0-6).
 * This replicates the original columnSpanToUseMapper from constants.js exactly,
 * ensuring legacy items render identically to how they did before this module existed.
 */
function computeSpanFromLegacy(storedSpan, boardColumns) {
    const legacyMap = {
        4: { 0: 1, 1: 1, 2: 2, 3: 3, 4: 4, 5: 3, 6: 4 },
        6: { 0: 1, 1: 2, 2: 4, 3: 4, 4: 6, 5: 5, 6: 6 },
    };
    const map = legacyMap[boardColumns];
    if (map && storedSpan in map) {
        return map[storedSpan];
    }
    return Math.max(1, Math.min(boardColumns, storedSpan));
}

/**
 * Prepares layout data for a single item (used when adding new items).
 * 
 * For bulk saves (Save Layout button), use getLayoutForSaveAll() instead,
 * which processes all items together with row-aware scaling.
 * 
 * This single-item version uses simple proportional scaling since it has
 * no row context. New items are typically placed at offset 0 so this is fine.
 * 
 * @param {object} boardItem - The item as represented in the Board component state
 * @param {number} screenWidth - Current viewport width
 * @returns {object} { column_span, column_offset, row_span } ready for API call
 */
export function getLayoutForSave(boardItem, screenWidth) {
    const boardColumns = getBoardColumnsForWidth(screenWidth);
    const renderedSpan = Number(boardItem.columnSpan) || 1;
    const rowSpan = boardItem.rowSpan;

    // Extract the current offset
    let currentOffset = extractOffset(boardItem.columnOffset, boardColumns);

    // Compute equivalents for other grid sizes using row-aware scaling
    // Since we only have one item here, use simple proportional scaling
    const spans = {};
    const offsets = {};
    for (const targetCols of SUPPORTED_BOARD_COLUMNS) {
        if (targetCols === boardColumns) {
            spans[targetCols] = renderedSpan;
            offsets[targetCols] = currentOffset;
        } else {
            const ratio = targetCols / boardColumns;
            spans[targetCols] = Math.max(1, Math.min(targetCols, Math.round(renderedSpan * ratio)));
            offsets[targetCols] = Math.max(0, Math.min(targetCols - spans[targetCols], Math.round(currentOffset * ratio)));
        }
    }

    const storageObj = { ...offsets, _spans: spans };
    let columnOffsetStr = JSON.stringify(storageObj).replaceAll('"', "'");

    return {
        column_span: String(renderedSpan),
        column_offset: columnOffsetStr,
        row_span: String(rowSpan),
    };
}

/**
 * Prepares layout data for ALL items together using row-aware scaling.
 * 
 * This is the primary save function. It groups items into rows based on
 * their current positions, then scales each row as a unit to compute
 * equivalent layouts for other screen sizes. This ensures items that are
 * side-by-side stay side-by-side, and deliberate gaps are preserved.
 * 
 * Output format per item:
 *   column_span: "3" (current screen's span, for backward compat with DB schema)
 *   column_offset: "{'4': 0, '6': 0, '_spans': {'4': 2, '6': 3}}"
 *     - Top-level keys (4, 6): the column offset for each grid size
 *     - _spans: the column span for each grid size
 * 
 * @param {Array} boardItems - All items as represented in the Board component state
 * @param {number} screenWidth - Current viewport width
 * @returns {Array} Array of { board_item_id, column_span, column_offset, row_span }
 */
export function getLayoutForSaveAll(boardItems, screenWidth) {
    const boardColumns = getBoardColumnsForWidth(screenWidth);

    // Build item descriptors with current span/offset
    const itemDescriptors = boardItems.map((item, index) => ({
        index,
        id: item.id,
        span: Number(item.columnSpan) || 1,
        offset: extractOffset(item.columnOffset, boardColumns),
        rowSpan: item.rowSpan,
    }));

    // Group into rows based on current layout
    const rows = groupIntoRows(itemDescriptors, boardColumns);

    // For each target column count, scale each row
    const results = new Array(boardItems.length);

    for (const item of itemDescriptors) {
        results[item.index] = {
            board_item_id: item.id,
            spans: { [boardColumns]: item.span },
            offsets: { [boardColumns]: item.offset },
            rowSpan: item.rowSpan,
        };
    }

    // Compute scaled values for other grid sizes
    for (const targetCols of SUPPORTED_BOARD_COLUMNS) {
        if (targetCols === boardColumns) continue;

        for (const row of rows) {
            const scaled = scaleRow(row, boardColumns, targetCols);
            for (const scaledItem of scaled) {
                results[scaledItem.index].spans[targetCols] = scaledItem.targetSpan;
                results[scaledItem.index].offsets[targetCols] = scaledItem.targetOffset;
            }
        }
    }

    // Format for API
    return results.map(r => {
        const storageObj = { ...r.offsets, _spans: r.spans };
        let columnOffsetStr = JSON.stringify(storageObj).replaceAll('"', "'");
        return {
            board_item_id: r.board_item_id,
            row_span: String(r.rowSpan),
            column_span: String(r.spans[boardColumns]),
            column_offset: columnOffsetStr,
        };
    });
}

/**
 * Extracts the numeric offset from the Board component's columnOffset object.
 */
function extractOffset(columnOffset, boardColumns) {
    if (columnOffset == null) return 0;
    if (typeof columnOffset === 'object') {
        const val = columnOffset[boardColumns] ?? columnOffset[String(boardColumns)];
        if (val != null) return Number(val) || 0;
        const values = Object.values(columnOffset);
        return values.length > 0 ? (Number(values[0]) || 0) : 0;
    }
    return Number(columnOffset) || 0;
}

/**
 * Parses the stored column_offset value from the database.
 */
function parseStoredOffset(rawOffset) {
    if (rawOffset == null) return null;
    if (typeof rawOffset === 'object') return rawOffset;
    if (typeof rawOffset === 'string') {
        if (!isNaN(Number(rawOffset)) && !rawOffset.includes('{')) {
            return null;
        }
        try {
            return JSON.parse(rawOffset.replaceAll("'", '"'));
        } catch {
            return null;
        }
    }
    return null;
}
